import { useSelector } from 'react-redux';
import { IStore } from '../interfaces/StoreTypes';
import { useConfigurationSections } from './useConfiguration';
import { CDN_BALANCER_BUCKETS, playerTypes } from '@libs/constants';
import npawPlugin from '../singletons/npawPluginSingleton';
import { getCdnBalancerProbingOptions } from '../utils/cdnBalancerProbingOptions';

declare global {
    interface Window {
        npawPlugin: typeof npawPlugin;
    }
}

// Track if we've already shown the warning to avoid spam
let warningShown = false;

const useCdnBalancer = () => {
    const { video } = useConfigurationSections();
    const cdnBalancerOptions = (video as any)?.cdnBalancerOptions;

    // Return loading state if configuration is not ready
    const isConfigReady = cdnBalancerOptions && cdnBalancerOptions.profileName;

    if (!isConfigReady) {
        if (!warningShown) {
            warningShown = true;
        }
        return {
            isConfigReady: false,
            initBalancer: undefined,
            setBucket: undefined,
        };
    }

    // Create safe balancer options with defaults
    const balancerOptions = {
        profileName: cdnBalancerOptions.profileName,
        balanceManifests: cdnBalancerOptions.balanceManifests ?? false,
        bucketName: cdnBalancerOptions.bucketName?.[CDN_BALANCER_BUCKETS.VOD] ?? 'vod',
        isLive: false,
        domainWhitelistRegex: cdnBalancerOptions.domainWhitelistRegex || [], //['pop.+\\.cdn\\.intigral-ott\\.net', "edgio.+\\.cdn\\.intigral-ott\\.net", "akm.+\\.cdn\\.intigral-ott\\.net", "fastly.+\\.cdn\\.intigral-ott\\.net"]
        stripPathForDomainRegex: cdnBalancerOptions.stripPathForDomainRegex || [], //["edgio.+\\.cdn\\.intigral-ott\\.net", "akm.+\\.cdn\\.intigral-ott\\.net", "fastly.+\\.cdn\\.intigral-ott\\.net"]
        stripPathRegex: cdnBalancerOptions.stripPathRegex || '', //"/bpk-token/[^/]+/"
        // e.g. `probeOnlyOnBanned: true` stops probes on a torn-down player's bpk-token
        ...getCdnBalancerProbingOptions(cdnBalancerOptions),
    };

    const initBalancer = () => {
        // Only initialize if we have valid options
        if (!cdnBalancerOptions) return;

        npawPlugin.setBalancerOptions(balancerOptions);
        npawPlugin.setLogLevel(cdnBalancerOptions?.logLevel ?? 6);
        window.npawPlugin = npawPlugin;
    };

    const setBucket = (playerType: string) => {
        // Only set bucket if we have valid options
        if (!cdnBalancerOptions) return;

        let bucketName = CDN_BALANCER_BUCKETS.VOD;
        if (playerType === playerTypes.LiveTV) {
            bucketName = CDN_BALANCER_BUCKETS.LIVE;
        }
        npawPlugin.setBalancerOptions({
            ...balancerOptions,
            bucketName: cdnBalancerOptions?.bucketName?.[bucketName] ?? 'vod',
            isLive: bucketName == CDN_BALANCER_BUCKETS.LIVE,
        });
        window.npawPlugin = npawPlugin;
    };

    return {
        isConfigReady: true,
        initBalancer,
        setBucket,
    };
};

export default useCdnBalancer;
